import { ref, computed } from 'vue'
import { initApiService } from '@/services/init'
import { authProvider } from '@/services/currentAuthProvider'
import { refreshIdToken } from '@/services/sessionExpiry'
import type { UserOrganizationSummary } from '@/api/generated-users'
import { logUnexpectedFailure } from '@/utils/errorHandling'

/**
 * Composable for managing organization state.
 *
 * Provides functionality to:
 * - Fetch the list of organizations the current user belongs to
 * - Get the currently active organization
 * - Switch to a different organization (with token refresh and page reload)
 *
 * Usage:
 * ```vue
 * <script setup>
 * import { useOrganization } from '@/composables/useOrganization'
 *
 * const {
 *   organizations,
 *   activeOrganization,
 *   hasMultipleOrganizations,
 *   isLoading,
 *   isSwitching,
 *   fetchOrganizations,
 *   switchOrganization
 * } = useOrganization()
 *
 * onMounted(() => fetchOrganizations())
 * </script>
 * ```
 */
export function useOrganization() {
  const organizations = ref<UserOrganizationSummary[]>([])
  const activeOrganization = ref<UserOrganizationSummary | null>(null)
  const isLoading = ref(false)
  const isSwitching = ref(false)

  // The API only returns organizations the user may actually switch into — a disabled
  // membership is left out server-side (#3638) — so the count is a fair test of whether
  // there is anything to switch between. Do not add client-side filtering here: that
  // would leave every other consumer of the endpoint with the unusable entries.
  const hasMultipleOrganizations = computed(() => organizations.value.length > 1)

  /**
   * Fetches the list of organizations the current user belongs to.
   * Also sets the active organization based on the API response.
   *
   * Gracefully handles 401 errors which can occur during auth state transitions
   * (e.g., when the component mounts before the auth flow is complete).
   * In such cases, the function returns silently without throwing.
   */
  async function fetchOrganizations(): Promise<void> {
    // Don't attempt API call if user is not authenticated
    // This prevents 401 errors during auth transitions (Fixes SCRIVIO-FE-2S)
    if (!authProvider.currentSession()) {
      return
    }

    isLoading.value = true
    try {
      const api = initApiService()
      const response = await api.users.listMyOrganizations()
      organizations.value = response.data.organizations
      activeOrganization.value =
        response.data.organizations.find(
          (org) => org.id === response.data.activeOrganizationId
        ) ?? null
    } catch (err: unknown) {
      // Gracefully handle auth errors - these can occur during auth state transitions
      // when the token isn't ready yet. The caller can retry once auth is complete.
      const axiosError = err as { response?: { status?: number } }
      if (axiosError.response?.status === 401) {
        console.debug('fetchOrganizations: auth not ready, will retry when authenticated')
        return
      }
      // For non-401 errors, log but don't throw to prevent unhandled rejections
      logUnexpectedFailure('Failed to fetch organizations:', err)
    } finally {
      isLoading.value = false
    }
  }

  /**
   * Switches to a different organization.
   *
   * This will:
   * 1. Call the API to update the active organization
   * 2. Force a token refresh to get new custom claims
   * 3. Redirect to dashboard to refresh all data with the new org context
   */
  async function switchOrganization(organizationId: string): Promise<void> {
    isSwitching.value = true
    try {
      const api = initApiService()
      await api.users.switchActiveOrganization({ organizationId })

      // Force token refresh to get new claims
      await refreshIdToken()

      // Redirect to dashboard - current page data may not exist in new org
      window.location.href = '/dashboard'
    } finally {
      isSwitching.value = false
    }
  }

  return {
    organizations,
    activeOrganization,
    hasMultipleOrganizations,
    isLoading,
    isSwitching,
    fetchOrganizations,
    switchOrganization,
  }
}
